<template>
    <tr
        :class="{
            selected: isSelected,
            clickable: isClickable,
            checked: selectable && isChecked,
            expanded: expanded,
            'parent-row': hasChildren,
            'child-row': item._isChild,
            'search-match': isSearchMatch,
            'gap-row': item._isGap,
            'row-even': isEvenRow,
            'disabled-row': isDisabled,
            'has-gps-data': item.hasGpsData,
            'has-border':
                !!borderColor && (Array.isArray(borderColor) ? borderColor.length > 0 : true),
        }"
        :style="{
            '--indent-level': item._level || 0,
            ...borderStyles,
        }"
        :data-border-count="borderCount"
        :id="item._id"
        @click="handleRowClick($event)"
        v-on="!isMobile ? { mouseenter: handleRowHover, mouseleave: handleRowLeave } : {}"
    >
        <td
            v-if="showCounter"
            class="counter-column text-center"
            :class="{
                indented: item._isChild,
            }"
        >
            {{ index }}
        </td>
        <td
            v-for="(column, colIndex) in columns"
            :key="colIndex"
            :data-priority="column.priority || 1"
            :class="[
                column.align ? `text-${column.align}` : '',
                { 'first-column': colIndex === 0 },
                { indented: colIndex === 0 && item._isChild },
            ]"
            :style="getColumnStyle(column)"
            v-tooltip="getColumnTooltip(column)"
        >
            <template v-if="colIndex === 0 && !item._isGap">
                <span
                    v-if="hasChildren || hasAnyExpandableRows"
                    :class="[
                        hasChildren ? 'expand-toggle' : 'expand-toggle-spacer',
                        { 'is-expanded': expanded },
                    ]"
                    @click.stop="hasChildren && toggleExpand()"
                >
                    <i v-if="hasChildren" :class="'ri-arrow-right-s-line'"></i>
                </span>
            </template>

            <template v-if="column.component">
                <component
                    :is="resolveComponent(column.component)"
                    v-bind="getComponentProps(column)"
                    @input="handleComponentInput($event, column)"
                />
            </template>
            <template v-else>
                <slot
                    :name="`column-${column.key}`"
                    :item="item"
                    :value="getItemValue(column)"
                    :column="column"
                >
                    <span>{{ truncateText(getItemValue(column), cellTruncateLength) }}</span>
                </slot>
            </template>
        </td>

        <td v-if="selectable" class="checkbox-column">
            <label class="checkbox-wrapper">
                <input
                    type="checkbox"
                    class="checkbox-input"
                    :checked="isChecked"
                    @change="toggleItemChecked"
                    @click.stop
                />
                <span class="checkmark" v-if="isChecked">
                    <i class="ri-check-line"></i>
                </span>
            </label>
        </td>
    </tr>
</template>

<script>
export default {
    name: 'BaseDashboardTableRow',
    props: {
        item: {
            type: Object,
            required: true,
        },
        index: {
            type: Number,
            required: true,
        },
        dataIndex: {
            type: Number,
            default: null,
        },
        columns: {
            type: Array,
            required: true,
        },
        isClickable: {
            type: Boolean,
            default: true,
        },
        selectable: {
            type: Boolean,
            default: false,
        },
        hasAnyExpandableRows: {
            type: Boolean,
            default: false,
        },
        isChecked: {
            type: Boolean,
            default: false,
        },
        expanded: {
            type: Boolean,
            default: false,
        },
        isSelected: {
            type: Boolean,
            default: false,
        },
        cellTruncateLength: {
            type: Number,
            default: 30,
        },
        globalComponents: {
            type: Object,
            default: () => ({}),
        },
        showCounter: {
            type: Boolean,
            default: true,
        },
        searchQuery: {
            type: String,
            default: '',
        },
        searchableColumns: {
            type: Array,
            default: () => [],
        },
        isDisabled: {
            type: Boolean,
            default: false,
        },
        borderColor: {
            type: [String, Array],
            default: null,
        },
    },
    data() {
        return {
            throttleTimeout: null,
            throttledHover: null,
            throttledLeave: null,
            getItemValueCache: new Map(), // Cache for getItemValue results
        };
    },
    mounted() {},
    watch: {
        item: {
            handler() {
                this.getItemValueCache.clear();
                if (this._componentPropsCache) {
                    this._componentPropsCache.clear();
                }
            },
            deep: false,
        },
        searchQuery: {
            handler() {
                const keysToDelete = [];
                for (const key of this.getItemValueCache.keys()) {
                    if (key.startsWith('search_')) {
                        keysToDelete.push(key);
                    }
                }
                keysToDelete.forEach((key) => this.getItemValueCache.delete(key));
            },
        },
        columns: {
            handler() {
                if (this._resolvedComponents) {
                    this._resolvedComponents.clear();
                }
                if (this._componentPropsCache) {
                    this._componentPropsCache.clear();
                }
            },
            deep: true,
        },
    },
    beforeDestroy() {},
    computed: {
        hasChildren() {
            return (
                (this.item.children && this.item.children.length > 0) ||
                this.item._hasChildren === true
            );
        },
        borderStyles() {
            if (!this.borderColor) return {};

            const colors = Array.isArray(this.borderColor) ? this.borderColor : [this.borderColor];
            const styles = {
                '--border-color': colors[0] || null,
            };

            colors.forEach((color, index) => {
                styles[`--border-color-${index + 1}`] = color;
            });

            return styles;
        },
        borderCount() {
            if (!this.borderColor) return 0;
            return Array.isArray(this.borderColor) ? this.borderColor.length : 1;
        },
        isSearchMatch() {
            if (!this.item._isChild) {
                return false;
            }

            if (!this.searchQuery || this.searchQuery.trim() === '') {
                return false;
            }

            const lowerCaseQuery = this.searchQuery.toLowerCase().trim();
            const columnsToCheck =
                this.searchableColumns.length > 0
                    ? this.searchableColumns
                    : this.columns.map((col) => col.key);

            const searchCacheKey = `search_${lowerCaseQuery}_${columnsToCheck.join(',')}`;

            if (this.getItemValueCache.has(searchCacheKey)) {
                return this.getItemValueCache.get(searchCacheKey);
            }

            let found = false;

            for (const columnKey of columnsToCheck) {
                const value = this.getItemValue({ key: columnKey });

                if (value != null && String(value).toLowerCase().includes(lowerCaseQuery)) {
                    found = true;
                    break;
                }
            }

            this.getItemValueCache.set(searchCacheKey, found);
            return found;
        },
        isEvenRow() {
            const rowIndex = this.dataIndex !== null ? this.dataIndex : this.index - 1;
            return rowIndex % 2 === 0;
        },
        isMobile() {
            return window.innerWidth <= 768;
        },
    },
    methods: {
        handleRowClick(event) {
            if (this.item._isGap) {
                return;
            }

            if (this.isDisabled) return;

            if (!this.isClickable) return;

            const selection = window.getSelection();
            if (selection && selection.toString().length > 0) {
                return;
            }

            const isInteractiveElement = event.target.closest('button, input, a, [role="button"]');

            if (isInteractiveElement && !isInteractiveElement.classList.contains('expand-toggle')) {
                return;
            }

            if (this.hasChildren && event.target.closest('.expand-toggle')) {
                this.toggleExpand();
                return;
            }

            this.$emit('row-click', { item: this.item, index: this.index, event });

            if (this.hasChildren || this.item._hasChildren) {
                this.toggleExpand();
            }
        },

        throttle(fn, delay = 50) {
            return (...args) => {
                if (this.throttleTimeout) return;
                this.throttleTimeout = setTimeout(() => {
                    fn.apply(this, args);
                    this.throttleTimeout = null;
                }, delay);
            };
        },

        handleRowHover() {
            if (!this.throttledHover) {
                this.throttledHover = this.throttle(() => {
                    this.$emit('row-hover', { item: this.item, index: this.index });
                });
            }
            this.throttledHover();
        },

        handleRowLeave() {
            if (!this.throttledLeave) {
                this.throttledLeave = this.throttle(() => {
                    this.$emit('row-leave', { item: this.item, index: this.index });
                });
            }
            this.throttledLeave();
        },

        toggleExpand() {
            this.$emit('toggle-expand', { item: this.item, index: this.index });
        },

        toggleItemChecked() {
            if (!this.selectable) return;
            this.$emit('item-checked', this.item);
        },

        getItemValue(column) {
            const cacheKey = column.key;

            if (this.getItemValueCache.has(cacheKey)) {
                return this.getItemValueCache.get(cacheKey);
            }

            let value;

            if (column.key.includes('.')) {
                const keys = column.key.split('.');
                value = this.item;
                for (const key of keys) {
                    if (value === null || value === undefined) {
                        this.getItemValueCache.set(cacheKey, '');
                        return '';
                    }
                    value = value[key];
                }
            } else if (column.formatter && typeof column.formatter === 'function') {
                value = column.formatter(this.item[column.key], this.item);
            } else {
                value = this.item[column.key];
            }

            this.getItemValueCache.set(cacheKey, value);
            return value;
        },

        truncateText(text, maxLength = 30) {
            if (text === null || text === undefined) return '';

            const stringValue = String(text);

            if (stringValue.length <= maxLength) return stringValue;

            return stringValue.substring(0, maxLength - 3) + '...';
        },

        getColumnTooltip(column) {
            const value = this.getItemValue(column);

            if (value !== null && value !== undefined) {
                const stringValue = String(value);
                if (stringValue.length > this.cellTruncateLength) {
                    return stringValue;
                }
            }

            if (column.tooltip) {
                if (typeof column.tooltip === 'function') {
                    return column.tooltip(this.item);
                }
                return column.tooltip;
            }

            return null;
        },

        resolveComponent(component) {
            if (!this._resolvedComponents) {
                this._resolvedComponents = new Map();
            }

            if (this._resolvedComponents.has(component)) {
                return this._resolvedComponents.get(component);
            }

            let resolvedComponent = null;

            if (typeof component === 'string') {
                if (this.globalComponents && this.globalComponents[component]) {
                    resolvedComponent = this.globalComponents[component];
                } else if (
                    this.$root.$options.components &&
                    this.$root.$options.components[component]
                ) {
                    resolvedComponent = component;
                } else {
                    const parentComponents = this.$parent.$options.components || {};
                    if (parentComponents[component]) {
                        resolvedComponent = component;
                    }
                }

                if (!resolvedComponent) {
                    resolvedComponent = 'div';
                }
            } else {
                resolvedComponent = component;
            }

            this._resolvedComponents.set(component, resolvedComponent);
            return resolvedComponent;
        },

        getComponentProps(column) {
            const cacheKey = `props_${column.key}`;
            if (this._componentPropsCache && this._componentPropsCache.has(cacheKey)) {
                return this._componentPropsCache.get(cacheKey);
            }

            if (!this._componentPropsCache) {
                this._componentPropsCache = new Map();
            }

            const value = this.getItemValue(column);
            const defaultProps = {
                value: value,
                item: this.item,
                column: column,
            };

            let finalProps = defaultProps;
            if (column.props) {
                if (typeof column.props === 'function') {
                    finalProps = { ...defaultProps, ...column.props(this.item) };
                } else {
                    finalProps = { ...defaultProps, ...column.props };
                }
            }

            this._componentPropsCache.set(cacheKey, finalProps);
            return finalProps;
        },

        getColumnStyle(column) {
            const style = {};

            return style;
        },

        handleComponentInput(value, column) {
            this.$emit('update:item', {
                ...this.item,
                [column.key]: value,
            });
        },
    },
};
</script>

<style lang="scss" scoped>
tr {
    &.is-visible {
        transform: translateY(0) !important;
    }

    &:not(.is-visible) {
        will-change: opacity, transform;
    }

    &.selected {
        background-color: #dfe1ee;
    }

    &.clickable {
        cursor: pointer;
    }

    &.disabled-row {
        opacity: 0.6 !important;
        pointer-events: none !important;
        background-color: rgba(0, 0, 0, 0.02) !important;
        border-left: 3px solid rgba(0, 0, 0, 0.02) !important;

        &:hover {
            background-color: rgba(0, 0, 0, 0.02) !important;
        }

        td {
            color: var(--color-text-inactive) !important;
        }
    }

    &.checked {
        background-color: rgba(var(--color-blue-rgb), 0.1);
    }

    &.expanded {
        background-color: rgba(var(--color-blue-rgb), 0.05);
    }

    &.parent-row {
        font-weight: 500;
        transform: translate(0);
        clip-path: inset(0);

        &.is-expanded {
            background-color: rgba(var(--color-blue-rgb), 0.03);
        }
    }

    &.child-row {
        transform: translate(0);
        clip-path: inset(0);
        padding-left: 0px;
        background-color: rgba(255, 193, 7, 0.01);
        border-left: 0px solid rgba(255, 193, 7, 0.3);

        &.row-even {
            background-color: rgba(255, 193, 7, 0.18) !important;
        }

        &:hover {
            background-color: rgba(255, 193, 7, 0.2) !important;
        }

        &.row-even:hover {
            background-color: rgba(255, 193, 7, 0.25) !important;
        }

        td:first-child {
            position: relative;

            &::after {
                content: '↳';
                position: absolute;
                left: 20px;
                top: 50%;
                transform: translateY(-50%);
                color: rgba(157, 124, 58, 1);
                font-size: 0.8rem;
                font-weight: bold;
                z-index: 2;
                opacity: 0.7;
            }
        }

        &[style*='--indent-level: 1'] {
            background-color: rgba(255, 193, 7, 0.05);
        }
    }

    &.gap-row {
        cursor: default !important;
        background-color: #ffe6c8 !important;

        &:hover {
            background-color: #ffe6c8 !important;
        }
    }

    &.has-gps-data {
        transform: translate(0);
        clip-path: inset(0);
    }

    &::selection {
        background-color: rgba(var(--color-blue-rgb), 0.3);
        color: var(--color-text-black);
    }

    &::-moz-selection {
        background-color: rgba(var(--color-blue-rgb), 0.3);
        color: var(--color-text-black);
    }
}

td {
    padding: 0px 8px;
    font-size: 0.85rem;
    color: var(--color-text-black);

    @media (max-width: 768px) {
        padding: 16px 12px;
        font-size: 0.9rem;
    }

    &.first-column {
        display: inline-flex;
        align-items: center;
        gap: 8px;
    }

    &.text-right {
        text-align: right;
    }

    &.text-center {
        text-align: center;
    }

    &::selection {
        background-color: rgba(var(--color-blue-rgb), 0.3);
        color: var(--color-text-black);
    }

    &::-moz-selection {
        background-color: rgba(var(--color-blue-rgb), 0.3);
        color: var(--color-text-black);
    }

    .expand-toggle,
    .checkbox-wrapper,
    button,
    input,
    a {
        user-select: none;
    }

    .expand-toggle,
    .expand-toggle-spacer {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        border-radius: 4px;
        cursor: pointer;
        transition: all 0.2s ease;

        i {
            font-size: 1.2rem;
            color: var(--color-gray);
            transition: transform 0.2s ease;
            transform: rotate(0deg);
        }

        &:hover {
            background-color: rgba(var(--color-blue-rgb), 0.1);

            i {
                color: var(--color-blue);
            }
        }

        &.is-expanded i {
            transform: rotate(90deg);
        }
    }

    .expand-toggle-spacer {
        pointer-events: none;
    }

    &.sticky-column {
        @media screen and (max-width: 768px) {
            position: sticky;
            left: 0;
            z-index: 10;
            background-color: var(--color-white);

            span {
                max-width: 120px;
            }
            &::after {
                content: '';
                position: absolute;
                right: 0;
                top: 0;
                bottom: 0;
                width: 1px;
                background-color: rgba(0, 0, 0, 0.1);
                pointer-events: none;
            }
        }
    }
}

.checkbox-column {
    width: 40px;
    text-align: center;
    padding: 10px 4px;

    @media (max-width: 768px) {
        padding: 16px 8px;
    }
}

.checkbox-wrapper {
    position: relative;
    display: inline-block;
    width: 18px;
    height: 18px;
    cursor: pointer;

    .checkbox-input {
        position: absolute;
        opacity: 0;
        cursor: pointer;
        height: 0;
        width: 0;

        &:checked ~ .checkmark {
            background-color: var(--color-blue);
            border-color: var(--color-blue);
        }
    }

    .checkmark {
        position: absolute;
        top: 0;
        left: 0;
        height: 18px;
        width: 18px;
        background-color: white;
        border: 0.5px solid rgba(0, 0, 0, 0.2);
        border-radius: 4px;
        transition: all 0.2s ease;
        display: flex;
        align-items: center;
        justify-content: center;

        i {
            color: white;
            font-size: 0.9rem;
        }
    }

    &:hover .checkbox-input ~ .checkmark {
        border-color: var(--color-blue);
    }
}

.counter-column {
    position: sticky;
    left: 0;
    z-index: 2;

    &.indented {
        padding-left: calc(8px + (var(--indent-level, 0) * 20px));
    }

    @media (max-width: 768px) {
        padding: 16px 12px;
    }
}

tr {
    transform: translate(0);
    clip-path: inset(0);

    &.has-border {
        transform: translate(0);
        clip-path: inset(0);

        &::before {
            content: '';
            position: absolute;
            left: 0;
            top: 1px;
            bottom: 1px;
            width: 4px;
            background: var(--border-color-1);
            border-radius: 0 2px 2px 0;
            z-index: 1;
            opacity: 1;
            transition: opacity 0.2s ease;
        }

        &[data-border-count='2']::before {
            background: linear-gradient(
                to bottom,
                var(--border-color-1) 50%,
                var(--border-color-2) 50%
            );
        }

        &[data-border-count='3']::before {
            background: linear-gradient(
                to bottom,
                var(--border-color-1) 33%,
                var(--border-color-2) 33% 66%,
                var(--border-color-3) 66%
            );
        }

        &[data-border-count='4']::before {
            background: linear-gradient(
                to bottom,
                var(--border-color-1) 25%,
                var(--border-color-2) 25% 50%,
                var(--border-color-3) 50% 75%,
                var(--border-color-4) 75%
            );
        }

        &[data-border-count='5']::before {
            background: linear-gradient(
                to bottom,
                var(--border-color-1) 20%,
                var(--border-color-2) 20% 40%,
                var(--border-color-3) 40% 60%,
                var(--border-color-4) 60% 80%,
                var(--border-color-5) 80%
            );
        }
    }
}
</style>
